import { useState } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import {
  ArrowLeft,
  Mail,
  Book,
  ChevronDown,
  ChevronUp,
  Send,
  Loader2,
  CheckCircle,
  Github,
  AlertCircle
} from 'lucide-react'

const API_URL = typeof window !== "undefined" && window.location.hostname === "localhost" 
  ? "http://localhost:3001" 
  : "https://api.nexatice.dev"

interface FAQItem {
  question: string
  answer: string
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Which databases does Nexatice support?',
    answer:
      'Nexatice supports MySQL, PostgreSQL, MongoDB, SQLite, Redis, Oracle, and NeonDB. Each database has tailored features - SQL databases get a table browser with inline editing, MongoDB has a document browser, Redis has a dedicated Key Browser, Oracle supports wallet-based authentication for cloud databases, and NeonDB works seamlessly with automatic SSL.'
  },
  {
    question: 'How do I connect to my database?',
    answer:
      'Click the "+" button next to Connections in the sidebar, select your database type, and enter your connection details (host, port, username, password). You can test the connection before saving. SSH tunnels and SSL/TLS encryption are also supported.'
  },
  {
    question: 'How do I connect to Oracle Database?',
    answer:
      'Oracle requires the Oracle Instant Client installed on your system. Download it free from oracle.com, extract it to a folder, then set the path in Settings → Database → Oracle Client Path. Restart Nexatice after setting the path. For Oracle Cloud (Autonomous DB), also download your wallet and specify the wallet path in the connection settings.'
  },
  {
    question: 'I\'m getting "DPI-1047: Cannot locate Oracle Client library" error',
    answer:
      'This means Nexatice can\'t find the Oracle Instant Client. Go to Settings → Database → Oracle Client Path and set it to your Instant Client folder (e.g., C:\\oracle\\instantclient_21_x). Make sure you downloaded the Basic or Basic Light package from Oracle\'s website, not just the SDK.'
  },
  {
    question: 'I\'m getting "NJS-090: initOracleClient() was already called" error',
    answer:
      'This happens when the Oracle client path is changed while Nexatice is running. Simply restart Nexatice completely and the new path will be used. The Oracle client can only be initialized once per session.'
  },
  {
    question: 'Oracle connection works but I can\'t see my data',
    answer:
      'Make sure you\'re connected to the correct schema. Oracle uses uppercase identifiers by default - table "users" becomes "USERS". Also check that your user has SELECT privileges on the tables. If using a wallet for Oracle Cloud, ensure the wallet path is set correctly in your connection settings.'
  },
  {
    question: 'Is my data secure?',
    answer:
      'Yes. Nexatice runs entirely on your local machine. Your database credentials are encrypted and stored locally - we never transmit or store your data on external servers. All connections support SSL/TLS encryption for secure communication.'
  },
  {
    question: 'What features are included for free?',
    answer:
      'Everything you need for daily database work: unlimited connections, Query Editor with auto-completion, Data Browser with inline editing, Redis Key Browser, Change Review system, schema visualization, query history, and auto-updates.'
  },
  {
    question: 'What about Pro features?',
    answer:
      'Pro features (Performance Advisor, Governance, Backup & Restore, Secrets Manager) are visible in the app but still under development. They may not work as expected yet. Pro licensing will be available early 2026.'
  },
  {
    question: 'Why does Windows show a security warning?',
    answer:
      'Nexatice is not code-signed yet (signing certificates are expensive for indie developers). Windows SmartScreen may show a warning. Click "More info" then "Run anyway" to install. You can verify the file is safe by scanning it on VirusTotal.'
  },
  {
    question: 'How do updates work?',
    answer:
      'Nexatice checks for updates automatically when you launch the app. When an update is available, you\'ll see a notification. Click "Download" to get the update, then restart the app to install it.'
  },
  {
    question: 'How do I report a bug or request a feature?',
    answer:
      'Open an issue on our GitHub at github.com/ZepioCs/Nexatice-Releases/issues or use the contact form below. Please include your Nexatice version (found in Settings > About), your operating system, and steps to reproduce the issue.'
  },
  {
    question: 'Is Nexatice available on macOS or Linux?',
    answer:
      'Currently only Windows is supported. macOS and Linux versions are planned but don\'t have a release date yet. Sign up for our newsletter to be notified when they become available.'
  },
  {
    question: 'Can I run multiple queries at once?',
    answer:
      'Yes! Use "Run Selected" (Ctrl+Shift+Enter) to run only highlighted text, or "Run From Cursor" (Ctrl+Alt+Enter) to run from your cursor position to the end. For MySQL, you can also enable Multi-Statement Mode in connection settings.'
  }
]

export function Support() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message })
      })

      const data = await response.json()

      if (data.success) {
        setSubmitted(true)
        setName('')
        setEmail('')
        setSubject('')
        setMessage('')
      } else {
        setError(data.message || 'Failed to send message. Please try again.')
      }
    } catch (err) {
      setError('Network error. Please try again or email us directly at support@nexatice.dev')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="legal" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Support</h1>
            <p className="text-zinc-400">Get help with Nexatice</p>
          </div>

          <div className="support-grid">
            <a href="/docs" className="support-card">
              <Book className="w-8 h-8 text-emerald-400" />
              <h3>Documentation</h3>
              <p>Read our comprehensive guides and tutorials</p>
            </a>
            <a
              href="https://github.com/ZepioCs/Nexatice-Releases/issues"
              target="_blank"
              rel="noopener noreferrer"
              className="support-card"
            >
              <Github className="w-8 h-8 text-zinc-400" />
              <h3>GitHub Issues</h3>
              <p>Report bugs and request features</p>
            </a>
            <a href="mailto:support@nexatice.dev" className="support-card">
              <Mail className="w-8 h-8 text-blue-400" />
              <h3>Email Support</h3>
              <p>support@nexatice.dev</p>
            </a>
          </div>

          <section className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, index) => (
                <div
                  key={index}
                  className={`faq-item ${openFaq === index ? 'open' : ''}`}
                >
                  <button className="faq-question" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                    <span>{item.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="contact-section">
            <h2>Contact Us</h2>
            <p className="section-description">
              Can't find what you're looking for? Send us a message and we'll get back to you within 24 hours.
            </p>

            {submitted ? (
              <div className="contact-success">
                <CheckCircle className="w-12 h-12 text-emerald-500" />
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll respond to your inquiry as soon as possible.</p>
                <button onClick={() => setSubmitted(false)} className="text-link">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Question</option>
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing & Licensing</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="enterprise">Enterprise Inquiry</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <button type="submit" className="contact-submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

